import { Logger } from '@eluve/logger';
import { isAudioFileChunkFile } from '@eluve/utils';

import { getAudioDurationFromFile } from './getAudioDurationFromFile';
import { readAppointmentFilesFromOpfs } from './opfs';
import { UserFileSystem } from './user-file-system.store';

interface FileCacheEntry {
  duration: number;
  fileSize: number;
  lastModified: number;
}

interface CalculateTotalDurationOptions {
  userFileSystem: UserFileSystem;
  appointmentId: string;
  logger?: Logger;
}

const fileDurationCache = new Map<string, FileCacheEntry>();

const getFileCacheKey = ({
  appointmentId,
  fileName,
  fileSize,
  lastModified,
}: {
  appointmentId: string;
  fileName: string;
  fileSize: number;
  lastModified: number;
}): string => {
  return `${appointmentId}:${fileName}:${fileSize}:${lastModified}`;
};

const getCachedFileDuration = async ({
  file,
  cacheKey,
  logger,
}: {
  file: File;
  cacheKey: string;
  logger?: Logger;
}): Promise<number> => {
  const cachedEntry = fileDurationCache.get(cacheKey);

  if (
    cachedEntry &&
    cachedEntry.fileSize === file.size &&
    cachedEntry.lastModified === file.lastModified
  ) {
    return cachedEntry.duration;
  }

  try {
    const duration = await getAudioDurationFromFile(file);
    const calculatedDuration = duration ?? 0;

    if (calculatedDuration > 0) {
      fileDurationCache.set(cacheKey, {
        duration: calculatedDuration,
        fileSize: file.size,
        lastModified: file.lastModified,
      });
    }

    return calculatedDuration;
  } catch (error) {
    logger?.warn('Failed to get audio duration from file in cache', {
      error,
      cacheKey,
    });
    return 0;
  }
};

export const calculateTotalDuration = async ({
  userFileSystem,
  appointmentId,
  logger,
}: CalculateTotalDurationOptions): Promise<number> => {
  if (!appointmentId) {
    return 0;
  }

  let updatedTotalDuration = 0;
  const readResponse = await readAppointmentFilesFromOpfs({
    userFileSystem,
    appointmentId,
    logger,
  });

  if (readResponse.type !== 'directoryExists') {
    return 0;
  }

  for (const fileRow of readResponse.files) {
    if (isAudioFileChunkFile(fileRow.name)) {
      continue;
    }

    if (fileRow.file.size === 0) {
      logger?.info('File is empty, skipping duration calculation', {
        appointmentId,
        fileName: fileRow.name,
      });
      continue;
    }

    try {
      const cacheKey = getFileCacheKey({
        appointmentId,
        fileName: fileRow.name,
        fileSize: fileRow.file.size,
        lastModified: fileRow.file.lastModified,
      });

      const duration = await getCachedFileDuration({
        file: fileRow.file,
        cacheKey,
        logger,
      });

      if (duration) {
        updatedTotalDuration += duration;
      }
    } catch (error) {
      logger?.warn('Failed to get audio duration from file', {
        error,
        errorMessage: (error as Error)?.message,
        fileName: fileRow.name,
        fileSize: fileRow.file.size,
        appointmentId,
      });
    }
  }

  return updatedTotalDuration;
};

// Export cache management functions
export const clearCalculateTotalDurationCache = (): void => {
  fileDurationCache.clear();
};

export const clearAppointmentFilesCache = (appointmentId: string): void => {
  for (const [key] of fileDurationCache) {
    if (key.startsWith(`${appointmentId}:`)) {
      fileDurationCache.delete(key);
    }
  }
};
